import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  Modal,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import { v4 as uuidv4 } from "uuid";

import { makeRequest } from "../../util";
import { SaaSDestination } from "../destinations";
import { ModalField } from "./ConfigureIntegrationModal";
import { useModalStyles } from "./styles";

const getConnectionModeFieldForDestinations = (
  destination: SaaSDestination
) => {
  const clientSideDisabled = destination.connectionMode === "server";
  const serverSideDisabled =
    destination.connectionMode === "client" && !destination.supportsServerSide;
  return {
    name: "Configure Connection Mode",
    key: "connectionMode",
    required: false,
    type: "radio",
    options: [
      {
        label: `Client-side ${
          clientSideDisabled ? `(not supported for ${destination.name})` : ""
        }`,
        value: "client",
        disabled: clientSideDisabled,
      },
      {
        label: `Server-side ${
          serverSideDisabled ? `(not supported for ${destination.name})` : ""
        }`,
        value: "server",
        disabled: serverSideDisabled,
      },
    ],
    defaultValue: destination.connectionMode,
    description: "Select the connection mode for this destination.",
  };
};

export const ConnectionModeDestinationModal = ({
  open,
  onClose,
  onSave,
  dest,
  existingConfig,
}) => {
  const defaultValueMap = {
    connectionMode: dest.connectionMode,
  };

  const classes = useModalStyles();
  const alert = useAlert();

  const [values, setValues] = useState({
    ...defaultValueMap,
    connectionMode: existingConfig.connection_mode,
  });

  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const handleFieldChange = (key, value) => {
    setValues((currentValues) => ({
      ...currentValues,
      [key]: value,
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleSave = () => {
    const id = uuidv4();

    const defaultConfig = {
      // This id may be overwritten by existing config
      id: id,
      dest_type: dest.key,
      name: dest.key,
      connection_mode: dest.connectionMode,
    };

    const destConfig = {
      ...defaultConfig,
      ...existingConfig,
      // Remove any values that are just a blank string as that's a filler value
      // we use from the backend.
      settings: _.pickBy(existingConfig.settings, (value) => value !== ""),
      connection_mode: values.connectionMode,
    };

    makeRequest("save_destination_config", destConfig)
      .then(() => {
        onSave && onSave(dest, destConfig.id, destConfig);
        closeConfirmationModal();
        handleClose();
      })
      .catch((error) => {
        alert.error("Something went wrong. Unable to save destination.");
        console.log(error);
        Sentry.captureException(error);
      });
  };

  if (!open) {
    return null;
  }

  const connectionModeField = getConnectionModeFieldForDestinations(dest);

  const connectionModeModal = (
    <Modal onClose={handleClose} open={open}>
      <Card className={clsx(classes.root, classes.smallModal)}>
        <CardHeader
          title={`Connection Mode Settings: ${dest.name}`}
          classes={{ action: clsx(classes.closeIconAction) }}
          action={<CloseIcon onClick={onClose} />}
        />
        <Divider />
        <CardContent>
          <ModalField
            field={connectionModeField}
            values={values}
            settings={{}}
            handleFieldChange={handleFieldChange}
            checkboxEnabled={true}
          />
        </CardContent>
        <Divider />
        <CardActions className={classes.actions}>
          <Button onClick={handleClose}>Close</Button>
          {onSave && (
            <Button
              color="primary"
              onClick={() => {
                setConfirmationModalOpen(true);
              }}
              variant="contained"
              disabled={existingConfig.connection_mode == values.connectionMode}
            >
              Save
            </Button>
          )}
        </CardActions>
      </Card>
    </Modal>
  );

  let confirmationModal: JSX.Element | null = null;
  if (confirmationModalOpen) {
    const fromMode = `${_.capitalize(existingConfig.connection_mode)}-side`;
    const toMode = `${_.capitalize(values.connectionMode)}-side`;
    confirmationModal = (
      <Modal onClose={closeConfirmationModal} open={confirmationModalOpen}>
        <Card className={clsx(classes.root, classes.extraSmallModal)}>
          <CardHeader
            title="Confirm Switch Connection Mode"
            classes={{ action: clsx(classes.closeIconAction) }}
            action={<CloseIcon onClick={closeConfirmationModal} />}
          />
          <Divider />
          <CardContent>
            <Typography variant="body1">
              You&apos;re about to switch your connection mode from{" "}
              <b>{fromMode}</b> to <b>{toMode}</b>. There may be differences in
              the behavior of these two modes. Please refer to our{" "}
              <a
                className="underline"
                style={{ color: "blue" }}
                href="https://documentation.freshpaint.io/destinations"
                rel="noopener noreferrer"
                target="_blank"
              >
                destination docs
              </a>{" "}
              for more details.
              <br />
              <br />
              Are you sure you want to set connection mode to <b>{toMode}</b>?
            </Typography>
          </CardContent>
          <Divider />
          <CardActions className={classes.actions}>
            <Button onClick={closeConfirmationModal}>Cancel</Button>
            <Button
              className={clsx(classes.deactivateButton)}
              onClick={() => {
                handleSave();
              }}
              variant="contained"
            >
              Confirm
            </Button>
          </CardActions>
        </Card>
      </Modal>
    );
  }

  return (
    <>
      {connectionModeModal}
      {confirmationModal}
    </>
  );
};

ConnectionModeDestinationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  dest: PropTypes.object,
  existingConfig: PropTypes.object.isRequired,
};
